/**
 * Global Fetch Interceptor
 * 
 * Monkey-patches window.fetch to automatically inject the Supabase JWT
 * as an Authorization: Bearer header on all /api/* requests.
 * 
 * This is the least disruptive way to secure all API calls without
 * touching every service file. The x-user-id header continues to be
 * sent for backward compatibility during the transition period.
 * 
 * Install once at app startup (main.tsx) BEFORE any components render.
 */

import { supabase } from './supabase';

const originalFetch = window.fetch.bind(window);

async function interceptedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  // Only intercept /api/ calls (relative or absolute on same origin)
  const url = typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url;

  const isApiCall = url.startsWith('/api/') || url.includes('/api/');

  if (!isApiCall) {
    return originalFetch(input, init);
  }

  // Build new headers, preserving any existing ones
  const headers = new Headers(init?.headers);

  // Don't override if Authorization is already set (e.g. by getAuthHeaders())
  if (!headers.has('Authorization')) {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (session?.access_token) {
        headers.set('Authorization', `Bearer ${session.access_token}`);

        // Also set x-user-id for backward compatibility
        if (!headers.has('x-user-id') && session.user?.id) {
          headers.set('x-user-id', session.user.id);
        }
      }
    } catch {
      // Silently continue without auth — don't break the request
    }
  }

  return originalFetch(input, { ...init, headers });
}

/**
 * Install the fetch interceptor. Call once at app startup.
 * Safe to call multiple times — only installs once.
 */
let installed = false;
export function installFetchInterceptor(): void {
  if (installed) return;
  installed = true;
  window.fetch = interceptedFetch as typeof window.fetch;
}
